import { createTheme } from '@mui/material/styles';

/**
 * FlowInCash App Theme Configuration
 *
 * This theme uses Material-UI with FlowInCash's purple/emerald brand colors.
 * Brand colors match flowincash.app landing page design.
 *
 * Color Scheme:
 * - Primary (Flow Purple #7C3AED): Main brand color - used for primary actions
 * - Secondary (Cash Emerald #059669): Secondary brand color - used for positive decisions
 * - Error (Red): Warning, "don't buy" - used for negative decisions
 * - Warning (Yellow/Orange): Caution, "consider" - used for blue light decisions
 * - Background: Light emerald tint (#ECFDF5) for subtle brand presence
 */
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#7C3AED', // Flow Purple - primary brand color
      light: '#A78BFA',
      dark: '#6D28D9',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#059669', // Cash Emerald - secondary brand color
      light: '#10B981',
      dark: '#047857',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#dc3545', // Red - for "don't buy" decisions and errors
      light: '#e57373',
      dark: '#c82333',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#ffc107', // Yellow/Orange - for "consider alternatives" decisions
      light: '#FFD54F',
      dark: '#FFA000',
      contrastText: '#000000',
    },
    info: {
      main: '#17a2b8', // Teal - for informational messages
      light: '#4dd4e8',
      dark: '#117a8b',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#28a745',
      light: '#4CAF50',
      dark: '#1e7e34',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#ECFDF5', // Light emerald tint (from flowincash.app)
      paper: '#FFFFFF',   // White for cards and elevated surfaces
    },
    text: {
      primary: '#000000',
      secondary: '#333333',
      disabled: '#999999',
    },
    divider: '#E0E0E0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Headlines
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    // Body text
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: '0.01071em',
    },
    // Button text
    button: {
      textTransform: 'none', // Don't uppercase buttons (more friendly)
      fontWeight: 600,
      letterSpacing: '0.02857em',
    },
    // Caption and helper text
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8, // Slightly rounded corners for modern look
  },
  spacing: 8, // 8px base unit (standard Material Design)
  // Component-specific overrides
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.1rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
      },
      defaultProps: {
        disableElevation: true, // Flat buttons by default
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export default theme;
